import { useMemo, useState } from "react";
import {
  jumpHeightAtDistance,
  type PlatformerJumpConfig,
  type PlatformerJumpInstance,
  type PlatformerJumpSubmission,
} from "../../puzzles/core/platformerJump";
import { Button, Card, Pill } from "../ui";
import { Icon } from "../Icon";

export function PlatformerJumpPlayer({
  config,
  instance,
  onLock,
}: {
  config: PlatformerJumpConfig;
  instance: PlatformerJumpInstance;
  onLock(submission: PlatformerJumpSubmission): void;
}) {
  const [gravity, setGravity] = useState(10);
  const [launchVelocity, setLaunchVelocity] = useState(15);
  const [simulationProgress, setSimulationProgress] = useState<number | null>(
    null,
  );
  const pathPoints = useMemo(() => {
    const totalFlight = (2 * launchVelocity) / gravity;
    const maxDistance = totalFlight * config.horizontalSpeed;
    const result: Array<{ x: number; y: number }> = [];
    for (let distance = 0; distance <= maxDistance; distance += 0.4) {
      const height = jumpHeightAtDistance(
        distance,
        gravity,
        launchVelocity,
        config.horizontalSpeed,
      );
      result.push({ x: 55 + distance * 16, y: 290 - height * 22 });
    }
    return result;
  }, [config.horizontalSpeed, gravity, launchVelocity]);
  const path = pathPoints.map((point) => `${point.x},${point.y}`).join(" ");
  const apex = (launchVelocity * launchVelocity) / (2 * gravity);
  const flightTime = (2 * launchVelocity) / gravity;
  const landing = flightTime * config.horizontalSpeed;
  const isSimulating = simulationProgress !== null;
  const heroPoint =
    simulationProgress === null
      ? { x: 55, y: 278 }
      : (() => {
          const point =
            pathPoints[
              Math.min(
                pathPoints.length - 1,
                Math.round((pathPoints.length - 1) * simulationProgress),
              )
            ] ?? { x: 55, y: 290 };
          return { x: point.x, y: point.y - 12 };
        })();
  const lockSubmission = () => {
    if (isSimulating) return;
    runSimulation(setSimulationProgress, () =>
      onLock({ gravity, launchVelocity }),
    );
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[.65fr_1.35fr]">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">
            Motion tuner
          </h3>
          <Pill tone="amber">Kinematics</Pill>
        </div>
        <p className="mt-2 text-xs leading-5 text-slate-400">
          Clear the red obstacle and land inside the green platform. Great math
          should also produce great game feel.
        </p>
        <div className="mt-6 space-y-6">
          <Control
            label="Gravity"
            value={gravity}
            suffix=" m/s²"
            min={config.gravityMin}
            max={config.gravityMax}
            step={0.2}
            disabled={isSimulating}
            onChange={setGravity}
          />
          <Control
            label="Launch velocity"
            value={launchVelocity}
            suffix=" m/s"
            min={config.velocityMin}
            max={config.velocityMax}
            step={0.2}
            disabled={isSimulating}
            onChange={setLaunchVelocity}
          />
        </div>
        <div className="mt-6 grid grid-cols-3 gap-2">
          <Readout label="Apex" value={`${apex.toFixed(1)}m`} />
          <Readout label="Air time" value={`${flightTime.toFixed(1)}s`} />
          <Readout label="Landing" value={`${landing.toFixed(1)}m`} />
        </div>
        <Button
          className="mt-6 w-full"
          onClick={lockSubmission}
          disabled={isSimulating}
        >
          <Icon name="Zap" size={18} />
          {isSimulating ? "Simulating jump..." : "Lock jump settings"}
        </Button>
      </Card>

      <Card className="overflow-hidden p-3 sm:p-5">
        <div className="mb-3 flex items-center justify-between px-2">
          <span className="font-retro text-[8px] tracking-widest text-slate-400">
            PLATFORMER FEEL LAB
          </span>
          <span className="text-[10px] font-bold text-cyan-300">
            Δt {config.simulationStep}s
          </span>
        </div>
        <svg
          viewBox="0 0 600 340"
          className="h-auto w-full rounded-2xl border border-slate-800 bg-slate-950"
          role="img"
          aria-label={`Jump arc with gravity ${gravity}, launch velocity ${launchVelocity}, and predicted landing at ${landing.toFixed(1)} meters.`}
        >
          <defs>
            <linearGradient id="jump-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#11213b" />
              <stop offset="1" stopColor="#08111f" />
            </linearGradient>
          </defs>
          <rect width="600" height="340" fill="url(#jump-sky)" />
          <circle cx="490" cy="65" r="28" fill="#fbbf24" opacity=".12" />
          <path
            d="M0 290 H380 M500 290 H600"
            stroke="#475569"
            strokeWidth="18"
          />
          <rect
            x={55 + instance.obstacleDistance * 16 - 20}
            y={290 - instance.obstacleHeight * 22}
            width="40"
            height={instance.obstacleHeight * 22}
            rx="5"
            fill="#ef4444"
            opacity=".8"
          />
          <rect
            x={55 + instance.landingMin * 16}
            y="277"
            width={(instance.landingMax - instance.landingMin) * 16}
            height="26"
            rx="7"
            fill="#10b981"
            opacity=".8"
          />
          <polyline
            points={path}
            fill="none"
            stroke="#22d3ee"
            strokeWidth="4"
            strokeDasharray="7 6"
          />
          <g transform={`translate(${heroPoint.x} ${heroPoint.y})`}>
            <rect x="-13" y="-26" width="26" height="26" rx="6" fill="#a5b4fc" />
            <circle cx="-5" cy="-17" r="2" fill="#0f172a" />
            <circle cx="5" cy="-17" r="2" fill="#0f172a" />
          </g>
          <text x="25" y="32" fill="#94a3b8" fontSize="11">
            Clear {instance.obstacleHeight}m · Land {instance.landingMin}-
            {instance.landingMax}m
          </text>
        </svg>
      </Card>
    </div>
  );
}

function Control({
  label,
  value,
  suffix,
  min,
  max,
  step,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  suffix: string;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange(value: number): void;
}) {
  return (
    <label className="block">
      <span className="flex justify-between text-xs font-bold text-slate-300">
        {label}
        <span className="font-retro text-[8px] text-amber-300">
          {value.toFixed(1)}
          {suffix}
        </span>
      </span>
      <input
        type="range"
        className="mt-3 w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/65 p-2.5 text-center">
      <p className="text-[8px] font-bold uppercase tracking-wider text-slate-600">
        {label}
      </p>
      <p className="mt-1 font-retro text-[8px] text-cyan-300">{value}</p>
    </div>
  );
}

function runSimulation(
  setProgress: (progress: number | null) => void,
  onComplete: () => void,
) {
  const duration = 1600;
  const startedAt = window.performance.now();

  const tick = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    setProgress(progress);
    if (progress < 1) {
      window.requestAnimationFrame(tick);
      return;
    }
    window.setTimeout(() => {
      setProgress(null);
      onComplete();
    }, 250);
  };

  setProgress(0);
  window.requestAnimationFrame(tick);
}
